import { BridgeComponent } from "@hotwired/hotwire-native-bridge";
import { playbackObservabilityValues, playbackObservabilityPayload } from "utils/playback_observability";

export default class VideoPlayerController extends BridgeComponent {
  static component = "video-player";

  // episodeId and userId are gone on purpose: neither was ever rendered, and viewer identity now
  // has exactly one spelling on this payload - `viewerId` in playbackObservabilityValues below,
  // resolved server-side and gated by Media::PlaybackSurface. Do not reintroduce a second one.
  static values = {
    timestamp: Number,
    markers: Array,
    fileType: String,
    autoplay: Boolean,
    allowNativePlay: Boolean,
    cdn: String,
    videoTitle: String,
    liveUrl: String,
    publishedAtMs: Number,
    serverNowMs: Number,
    duration: Number,
    awsHls: Boolean,
    hlsToken: String,
    ...playbackObservabilityValues
  }

  async connect() {
    let feedItemUrl = this.element.getAttribute("data-feed-item-url");
    let hlsPlaylist = this.element.getAttribute("data-playlist-url");
    let itemId = this.element.getAttribute('data-item-id');
    let itemType = this.element.getAttribute('data-item-type');
    let title = this.element.getAttribute('data-title');

    const timestamp = await this.getTimestamp(itemType, itemId);

    if (!this.element.isConnected) return;

    let originalUrl = this.element.getAttribute("data-original-url");
    let isAwsHls = this.awsHlsValue;
    let hlsToken = this.hlsTokenValue;

    this.send("connect", {
      feedItemUrl: feedItemUrl,
      hlsPlaylist: hlsPlaylist,
      originalUrl: originalUrl,
      // Sent as a number, not the raw getAttribute string: Android's ConnectData.itemId is
      // typed Long and kotlinx.serialization's default Json isn't lenient about a quoted
      // numeric string there, so a bare string here would fail JSON decode and silently
      // drop the whole connect payload (including the timestamp below) on Android.
      itemId: itemId != null ? Number(itemId) : itemId,
      itemType: itemType,
      title: title,
      timestamp: timestamp,
      isAwsHls: isAwsHls,
      hlsToken: hlsToken,
      // cdn and videoTitle were declared and rendered but never sent - native had to re-derive
      // both. They now ride inside playbackObservabilityPayload rather than being sent from
      // here: both are Mux dimensions (video_cdn, video_title), and neither key existed on this
      // payload before observability, so gating them is what keeps the flag-off payload
      // byte-for-byte identical to what it was. tokenType (in the same payload) carries the
      // same value as cdn for on-demand; both are sent because web sends both to Mux and native
      // mirrors web's shape.
      ...playbackObservabilityPayload(this),
    });
  }

  disconnect() {
    // Disonnect is key here because we want to hide video players when the page unloads. It's cleaner to 
    // do this from the rails side vs. the app since videos can be hosted in different contexts.
    this.send("disconnect");
  }

  // Mirrors the resume logic in media/video_player_controller.js#readyToPlay: prefer an explicit
  // ?timestamp (e.g. from PiP hand-off), otherwise fall back to the locally saved playback position.
  async getTimestamp(itemType, itemId) {
    if (this.hasTimestampValue && this.timestampValue > 0) {
      return this.timestampValue;
    }

    if (!window.playbackDb) return 0;

    const playbackData = await window.playbackDb.getPlaybackData(itemType, itemId);
    return playbackData ? playbackData.currentTime : 0;
  }
}
